import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Bell, BellOff, Plus, Radio, Zap } from "lucide-react";
import { api, type Channel, type ChannelDialog } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChannelAvatar } from "@/components/ChannelAvatar";
import { cn } from "@/lib/utils";
import { ChannelComparison } from "@/components/ChannelComparison";

export function Channels() {
  const query = useQuery({ queryKey: queryKeys.channels, queryFn: api.channels });
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Channels</h1>
        <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      {/* Two questions, not two screens: "what am I subscribed to" and "which of these is
          worth trading". The second used to mean opening every channel's analytics tab in
          turn and holding the numbers in your head. */}
      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">List</TabsTrigger>
          <TabsTrigger value="compare">Compare</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-3 space-y-2">
          {query.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {!query.isLoading && query.data?.channels.length === 0 && (
            <p className="text-sm text-muted-foreground">No channels yet.</p>
          )}

          {/* M26b — private channels (where the app TALKS) above signal channels (what it
              LISTENS to). Two rows versus potentially dozens; buried by insertion order the
              app's own targets were impossible to find. Archived channels are dropped here
              rather than server-side so the create dialog can keep seeing them (re-adding an
              archived channel revives the row, Channels create flow). */}
          {(() => {
            if (!query.data) return null;
            const alive = query.data.channels.filter((channel) => channel.archivedAt === null);
            const priv = alive.filter((channel) => channel.role !== "signal");
            const signal = alive.filter((channel) => channel.role === "signal");
            return (
              <>
                {priv.map((channel) => (
                  <ChannelRow key={channel.id} channel={channel} />
                ))}
                {priv.length > 0 && signal.length > 0 && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs font-medium text-muted-foreground">Signal channels</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
                {signal.map((channel) => (
                  <ChannelRow key={channel.id} channel={channel} />
                ))}
              </>
            );
          })()}
        </TabsContent>

        {/* Mounted only while selected, so the comparison query does not fire for everyone
            who opens the channel list. */}
        <TabsContent value="compare" className="mt-3">
          <ChannelComparison />
        </TabsContent>
      </Tabs>

      <CreateChannelDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/**
 * M26b — the status words became icons. Two text badges ("ingesting" + "auto-execute") ate
 * half the row on a phone and clipped every channel title. The M18 intent survives intact:
 * color is not a label, so each icon carries the exact words the badges used to show as its
 * accessible name, and a silenced notification target still reads as alarming (destructive
 * color AND the name) — switching one off because it "looked like it was ingesting" was a
 * real incident.
 */
export function ChannelRow({ channel }: { channel: Channel }) {
  const isPrivate = channel.role !== "signal";
  return (
    // Block, or the inline anchor swallows space-y-2's margins and the rows sit flush.
    <Link to={`/channels/${channel.id}`} className="block">
      <Card className={cn("transition-colors hover:border-primary/50", isPrivate && "border-border/60 bg-card/50")}>
        <CardContent className={cn("flex items-center justify-between gap-2", isPrivate ? "p-2.5" : "p-3")}>
          <div className="flex min-w-0 items-center gap-2.5">
            <ChannelAvatar src={channel.avatar} title={channel.title} size={isPrivate ? "sm" : undefined} />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{channel.title || `Channel ${channel.tgChannelId}`}</div>
              <div className="text-xs text-muted-foreground">{channel.role}</div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {channel.role === "signal" ? (
              <>
                <Radio
                  className={cn("h-4 w-4", channel.enabled ? "text-success" : "text-muted-foreground/40")}
                  aria-label={channel.enabled ? "ingesting" : "disabled"}
                />
                <Zap
                  className={cn("h-4 w-4", channel.autoExecute ? "text-primary" : "text-muted-foreground/40")}
                  aria-label={channel.autoExecute ? "auto-execute" : "manual"}
                />
              </>
            ) : channel.enabled ? (
              <Bell className="h-4 w-4 text-success" aria-label="notification target" />
            ) : (
              // M18 — for system/trades, `enabled` means "the app posts here", not
              // ingestion. A silenced target reads as alarming on purpose.
              <BellOff className="h-4 w-4 text-destructive" aria-label="notifications off" />
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * M15 — channels are enlisted from the live Telegram dialog list, never typed in by hand.
 * The list is what Andy's account is actually in (broadcast channels only), minus what is
 * already enlisted, so this is also how a clean-slate deploy gets its system and trades
 * channels: pick the role, tap Add.
 */
function CreateChannelDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<Channel["role"]>("signal");
  const [pendingId, setPendingId] = useState<number | null>(null);

  // Already cached from the page behind this dialog; used to grey out the system option
  // once a system channel exists (the server enforces it, this just says so up front).
  const channelsQuery = useQuery({ queryKey: queryKeys.channels, queryFn: api.channels });
  const systemTaken = (channelsQuery.data?.channels ?? []).some(
    (c) => c.role === "system" && c.archivedAt === null,
  );

  const dialogs = useQuery({
    queryKey: queryKeys.telegramDialogs,
    queryFn: api.telegramDialogs,
    enabled: open,
    // The picker must show a channel joined a minute ago; nothing here is worth caching.
    staleTime: 0,
  });

  const create = useMutation({
    mutationFn: (dialog: ChannelDialog) =>
      api.createChannel({ tgChannelId: dialog.tgChannelId, title: dialog.title, role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels });
      void queryClient.invalidateQueries({ queryKey: queryKeys.telegramDialogs });
      setPendingId(null);
    },
    onError: () => setPendingId(null),
  });

  const needle = search.trim().toLowerCase();
  const visible = (dialogs.data?.dialogs ?? []).filter(
    (d) =>
      needle === "" ||
      d.title.toLowerCase().includes(needle) ||
      (d.username ?? "").toLowerCase().includes(needle),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add from Telegram</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your channels…"
              className="flex-1"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Channel["role"])}
              className="rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Enlist as"
            >
              <option value="signal">signal</option>
              <option value="trades">trades</option>
              <option value="system" disabled={systemTaken}>
                {systemTaken ? "system — already enlisted" : "system"}
              </option>
            </select>
          </div>

          {create.isError && (
            <p className="text-sm text-destructive">
              {create.error instanceof Error ? create.error.message : "Could not enlist the channel."}
            </p>
          )}

          {role === "signal" && (
            <p className="text-xs text-muted-foreground">
              Signal channels are ingested for trading signals. Pick <span className="font-medium">system</span> or{" "}
              <span className="font-medium">trades</span> for your own notification channels.
            </p>
          )}

          {dialogs.isLoading && <Skeleton className="h-24 w-full" />}
          {dialogs.isError && (
            <p className="text-sm text-destructive">
              {dialogs.error instanceof Error ? dialogs.error.message : "Could not load dialogs."}
            </p>
          )}
          {dialogs.data && visible.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {dialogs.data.dialogs.length === 0
                ? "Every channel you are in is already enlisted."
                : "No channel matches the search."}
            </p>
          )}

          <div className="max-h-72 space-y-1 overflow-y-auto">
            {visible.map((dialog) => (
              <div
                key={dialog.tgChannelId}
                className="flex items-center justify-between gap-2 rounded-md border p-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <ChannelAvatar src={dialog.thumb} title={dialog.title} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {dialog.title || `Channel ${dialog.tgChannelId}`}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {dialog.username ? `@${dialog.username}` : dialog.tgChannelId}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={create.isPending}
                  onClick={() => {
                    setPendingId(dialog.tgChannelId);
                    create.mutate(dialog);
                  }}
                >
                  {create.isPending && pendingId === dialog.tgChannelId ? "Adding…" : `Add as ${role}`}
                </Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
